import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardHeader, CardBody, Row, Col, Table, Badge } from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";

// Loan & collection summary + transaction ledger for a payment (merchant-loan) customer. Replaces the
// old wallet "Credit History" block. Fully defensive: each call is independent and falls back to "—" /
// an empty table on any error, so it can never break the customer page.
const inr = (n) =>
  n === null || n === undefined || isNaN(Number(n))
    ? "—"
    : "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const isDebit = (t) => (t || "").toLowerCase() === "debit";

const Stat = ({ label, value }) => (
  <Col xs="6" md="3" className="mb-3">
    <div className="text-muted small text-uppercase" style={{ letterSpacing: "0.04em" }}>
      {label}
    </div>
    <div className="h2 mb-0">{value}</div>
  </Col>
);

const PaymentLoanSummary = () => {
  const { cid } = useParams();
  const [loanCount, setLoanCount] = useState(null);
  const [collected, setCollected] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!cid) return;
    let active = true;
    setLoading(true);
    const load = async () => {
      try {
        const r = await axiosInstance.get(URL.PAYMENT_LOANS, { params: { customerId: cid } });
        if (active) setLoanCount(Array.isArray(r.data) ? r.data.length : 0);
      } catch (e) {
        if (active) setLoanCount(null);
      }
      try {
        const r = await axiosInstance.get(URL.PAYMENT_COLLECTION_TOTAL, { params: { customer_id: cid } });
        if (active) setCollected(r.data && r.data.totalCollected != null ? r.data.totalCollected : 0);
      } catch (e) {
        if (active) setCollected(null);
      }
      // Ledger: this customer's payouts (debit) + collections (credit), newest first.
      try {
        const r = await axiosInstance.get(URL.PAYMENT_TRANSACTIONS, { params: { customer_id: cid } });
        if (active) setTxns(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (active) setTxns([]);
      }
      if (active) setLoading(false);
    };
    load();
    return () => {
      active = false;
    };
  }, [cid]);

  return (
    <Card className="bg-secondary shadow">
      <CardHeader className="bg-white border-0">
        <h3 className="mb-0">Loan &amp; Collection Summary</h3>
        <small className="text-muted">Customer #{cid}</small>
      </CardHeader>
      <CardBody>
        <Row className="mb-2">
          <Stat label="Total Loans" value={loading ? "…" : loanCount === null ? "—" : loanCount} />
          <Stat label="Total Collected" value={loading ? "…" : inr(collected)} />
          <Stat label="Transactions" value={loading ? "…" : txns.length} />
        </Row>

        <h4 className="mb-2 mt-2">Transaction Ledger</h4>
        <Table className="align-items-center table-flush" responsive bordered hover size="sm">
          <thead className="thead-light">
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Type</th>
              <th>Debit (Payout)</th>
              <th>Credit (Collection)</th>
              <th>Account / VPA</th>
              <th>UTR / Ref</th>
              <th>Merchant</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {txns.length > 0 ? (
              txns.map((t, i) => {
                const debit = isDebit(t.tranType);
                return (
                  <tr key={t.id ?? i}>
                    <td>{i + 1}</td>
                    <td>{fmtDate(t.createdAt)}</td>
                    <td>
                      <Badge color={debit ? "danger" : "success"} pill>
                        {debit ? "Payout (Debit)" : "Collection (Credit)"}
                      </Badge>
                    </td>
                    <td>{debit ? inr(t.amount) : "-"}</td>
                    <td>{debit ? "-" : inr(t.amount)}</td>
                    <td>
                      {t.accountNumber ? (
                        <div>A/c: {t.accountNumber}{t.ifscCode ? ` (${t.ifscCode})` : ""}</div>
                      ) : null}
                      {t.upiAccount ? <div>VPA: {t.upiAccount}</div> : null}
                      {!t.accountNumber && !t.upiAccount ? "-" : null}
                    </td>
                    <td>{t.utr || t.tranId || t.gatewayTransactionId || "-"}</td>
                    <td>{t.merchantCode || "-"}</td>
                    <td>{t.transactionStatus || "-"}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="text-muted">
                  {loading ? "Loading…" : "No transactions found"}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
};

export default PaymentLoanSummary;
